import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import {
  user,
  useGetPopularSearchListDataQuery,
  useUpdatePopularSearchHeadDataMutation,
  useUpdatePopularSearchStatusMutation,
} from "../../../store/queries/User";
import { deletePopularSearch } from "./api";
import { toast } from "react-toastify";
import { current } from "@reduxjs/toolkit";
import { getPopularSearchEditData, updateConfig } from "../../../store/slices/User/PopularSearch/popularSearchSlice";

const usePopularSearch = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
 
  const dispatch = useDispatch();
  const popularSearchState = useSelector((state) => state.popularSearch);
  const { selectedId } = useSelector((state) => state.popularSearch);
  const { showEditModal } = useSelector((state) => state.global);
  const {
    data: popularSearchList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetPopularSearchListDataQuery({
    page_size: popularSearchState?.currentPageSize,
    page: popularSearchState?.currentPage,
    sort: popularSearchState?.sortBy,
    sort_order: popularSearchState?.sortOrder,
    search: popularSearchState?.search,
  });
  const [updatePopularSearchFields] = useUpdatePopularSearchHeadDataMutation();
  const [updatePopularSearchStatus] = useUpdatePopularSearchStatusMutation();
  const handleCreateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = !state.showCreateModal;
        state.imagePreview = "";
        state.imageName = "";
        state.arabicImagePreview = "";
        state.arabicImageName = "";
        state.isEdit = false;
        state.editPopularSearchData = {};
      })
    );
  };
  useEffect(() => {
    if (popularSearchState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [popularSearchState.clearSelection]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };
  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleToggle = (data) => {
    const newStatus = data.status === 1 ? 2 : 1;
    let body = {
      search_id: data?._id,
      status: newStatus,
    };
    updatePopularSearchStatus(body);
    dispatch(
      user.util.updateQueryData(
        "getPopularSearchListData",
        {
          page_size: popularSearchState?.currentPageSize,
          page: popularSearchState?.currentPage,
          sort: popularSearchState?.sortBy,
          sort_order: popularSearchState?.sortOrder,
          search: popularSearchState?.search,
        },
        (cacheEntry) => {
          let currentCache = { ...current(cacheEntry) };
          const newResult = currentCache.data.result?.map((res) =>
            res._id === data?._id
              ? {
                  ...res,
                  status: newStatus,
                }
              : res
          );
          currentCache = {
            ...currentCache,
            data: {
              ...currentCache.data,
              result: newResult,
            },
          };
          return currentCache;
        }
      )
    );
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      start_date: (field, data) => <p className="pro-mb-0">{data.start_time}</p>,
      end_date: (field, data) => <p className="pro-mb-0">{data.end_time}</p>,
      status: (field, data) => {
        const isChecked = data?.status === 1;
        return (
          <div className="form-check form-switch ">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckDefault"
              checked={isChecked}
              onChange={(e) => handleToggle(data)}
            />
          </div>
        );
      },
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleSort = (label) => {
    if (popularSearchState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = popularSearchState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedId = e?.[0];
      })
    );
  };

  const handleDelete = async () => {
    const response = await deletePopularSearch(selectedId);
    if (response && response.status === 200) {
      toast.success(`Deleted successfully.`);
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.search="";
        })
      );
      setTimeout(() => {
        refetch();
      }, 0);
    } else {
      toast.error(`Something went wrong !`);
    }
    handleDeleteModal();
    dispatch(
      updateConfig((state) => {
        state.clearSelection = true;
      })
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };
  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };
 
  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = true;
        state.selectedId = e?.[0];
        state.isEdit = true;
      })
    );
    dispatch(getPopularSearchEditData(e?.[0]));
  };

  return {
    isLoading,
    isFetching,
    popularSearchList,
    showEditModal,
    popularSearchState,
    paginationOptions,
    handleEditColumnsClick,
    updatePopularSearchFields,
    handleDeleteModal,
    handleCreateClick,
    handlePagination,
    handleClearClick,
    handleEditAction,
    handlePageSize,
    handleSearch,
    handleSort,
    handleDelete,
    refetch,
    getRow,
  };
};

export default usePopularSearch;
